import { useState, useCallback } from 'react';
import { format } from 'date-fns';
import { Checkbox } from '@client/components/ui/checkbox';
import { Button } from '@client/components/ui/button';
import { getUserIpAddress } from '@/util/getUserIpAddress';

interface SeatInvitationConsentDialogProps {
  ownerName: string;
  ownerOrgName: string | null;
  expiresDateTime: Date | null;
  onAccept: (consentData: ConsentData) => void;
  isSubmitting?: boolean;
  /** Sent with a team invitation by a pilot inviter: worded as joining a team, with no subscription terms. */
  isTeamInvitation?: boolean;
}

interface ConsentData {
  hasConsented: boolean;
  ipAddress: string | null;
  userAgent: string;
}

export function SeatInvitationConsentDialog({
  ownerName,
  ownerOrgName,
  expiresDateTime,
  onAccept,
  isSubmitting = false,
  isTeamInvitation = false,
}: SeatInvitationConsentDialogProps) {
  const [hasConsented, setHasConsented] = useState(false);

  const handleAccept = useCallback(async () => {
    if (!hasConsented) return;

    let ipAddress: string | null = null;
    try {
      ipAddress = await getUserIpAddress();
    } catch (error) {
      console.error('Failed to retrieve IP address:', error);
    }

    // Capture consent metadata
    const consentData: ConsentData = {
      hasConsented: true,
      ipAddress,
      userAgent: navigator.userAgent,
    };

    onAccept(consentData);
  }, [hasConsented, onAccept]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-2xl font-semibold text-gray-900">
          {isTeamInvitation ? 'Accept Team Invitation' : 'Accept Subscription Invitation'}
        </h3>
        <p className="mt-1 text-gray-700">
          {isTeamInvitation
            ? `Review and accept the invitation to join ${ownerName}'s team`
            : 'Review and accept the invitation to join this subscription'}
        </p>
      </div>

      {/* Subscription Details - Simple two-column layout */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-gray-700">Organization</span>
          <span className="font-medium text-gray-900">{ownerOrgName || 'N/A'}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-gray-700">
            {isTeamInvitation ? 'Team Owner' : 'Subscription Owner'}
          </span>
          <span className="font-medium text-gray-900">{ownerName}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-gray-700">Access</span>
          <span className="font-medium text-gray-900">Full Platform Access</span>
        </div>
        {expiresDateTime && !isTeamInvitation && (
          <div className="flex items-center justify-between">
            <span className="text-gray-700">Expires</span>
            <span className="font-medium text-amber-600">
              {format(new Date(expiresDateTime), 'MMM d, yyyy')}
            </span>
          </div>
        )}
      </div>

      {/* Consent Description */}
      <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3">
        {isTeamInvitation ? (
          <p className="text-sm text-blue-700">
            By accepting this invitation, I agree to join {ownerName}'s team and will have access to
            the Waterlily platform. {ownerName} will not have access to my clients. I understand
            that
            {` ${ownerName}`} may remove me from the team at any time.
          </p>
        ) : (
          <p className="text-sm text-blue-700">
            By accepting this invitation, I agree to join the specified subscription and will have
            full access to the Waterlily platform. I understand that the subscription owner may
            remove me from the subscription at any time.
            {expiresDateTime && (
              <>
                {' '}
                This seat access will expire on {format(new Date(expiresDateTime), 'MMMM d, yyyy')}.
              </>
            )}
          </p>
        )}
      </div>

      {/* Consent Checkbox */}
      <div className="flex items-center gap-3">
        <Checkbox
          id="seat-consent"
          checked={hasConsented}
          onCheckedChange={checked => setHasConsented(checked === true)}
          disabled={isSubmitting}
          className="border-primary data-[state=checked]:bg-primary h-5 w-5 rounded data-[state=checked]:text-white"
        />
        <label
          htmlFor="seat-consent"
          className="cursor-pointer text-sm text-gray-700"
        >
          {isTeamInvitation
            ? 'I have read, understood, and agree to join this team.'
            : 'I have read, understood, and agree to join this subscription.'}
        </label>
      </div>

      {/* Action Button */}
      <div className="flex justify-center pt-2">
        <Button
          onClick={handleAccept}
          disabled={!hasConsented || isSubmitting}
          className="bg-primary w-full px-8 hover:bg-purple-700"
        >
          {isSubmitting
            ? 'Processing...'
            : isTeamInvitation
              ? 'Accept & Join Team'
              : 'Accept & Join Subscription'}
        </Button>
      </div>
    </div>
  );
}
